<template>
  <v-app-bar
    class="app-header"
    :class="{ 'header-hidden': isHidden }"
    :elevation="0"
    :height="126"
  >
    <top-bar />
    <v-container class="header-container">
      <!-- Logo -->
      <div class="logo-section">
        <router-link to="/" class="logo-link" @click="scrollToTop">
          <img src="@/assets/logo.png" alt="Invest Broker" class="logo-img" />
        </router-link>
      </div>

      <!-- Navigation Menu (desktop) -->
      <nav class="navigation-menu">
        <a href="#" class="nav-link" @click.prevent="scrollToTop">{{ $t('nav.start') }}</a>
        <a href="#services" class="nav-link" @click.prevent="scrollTo('#services')">{{ $t('nav.services') }}</a>
        <a href="#quality" class="nav-link" @click.prevent="scrollTo('#quality')">{{ $t('nav.quality') }}</a>
        <a href="#process" class="nav-link" @click.prevent="scrollTo('#process')">{{ $t('nav.process') }}</a>
        <a href="#partners" class="nav-link" @click.prevent="scrollTo('#partners')">{{ $t('nav.partners') }}</a>
        <a href="#answers" class="nav-link" @click.prevent="scrollTo('#answers')">{{ $t('nav.answers') }}</a>
      </nav>

      <!-- Right Section (desktop) -->
      <div class="right-section desktop-only">
        <button class="contact-btn" @click="openContact">{{ $t('nav.contact') }}</button>
        <v-menu
          v-model="langMenuOpen"
          :offset="[0, 8]"
          location="bottom end"
        >
          <template #activator="{ props }">
            <button class="lang-btn" v-bind="props">{{ currentLang }}</button>
          </template>
          <div class="lang-dropdown">
            <button
              v-for="lang in langs"
              :key="lang.code"
              class="lang-option"
              :class="{ active: currentLang === lang.code }"
              @click="selectLang(lang.code)"
            >{{ lang.label }}</button>
          </div>
        </v-menu>
      </div>

      <!-- Menu Button (mobile) -->
      <button class="menu-btn mobile-only" @click="openSidebar">{{ $t('nav.menu') }}</button>
    </v-container>
  </v-app-bar>

</template>

<script>
import TopBar from './TopBar.vue'
import { eventBus } from '@/eventBus'


export default {
  name: 'AppHeader',
  components: {
    TopBar
  },
  data() {
    return {
      isHidden: false,
      lastScrollY: 0,
      langMenuOpen: false,
      currentLang: 'Ro',
      langs: [
        { code: 'En', label: 'English' },
        { code: 'Ro', label: 'Română' },
        { code: 'Ru', label: 'Русский' }
      ]
    }
  },
  mounted() {
    const saved = localStorage.getItem('lang')
    if (saved) this.currentLang = saved
    window.addEventListener('scroll', this.onScroll, { passive: true })
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    onScroll() {
      const currentY = window.scrollY
      if (eventBus.showContactModal || eventBus.showServiceModal) {
        this.lastScrollY = currentY
        return
      }
      this.isHidden = currentY > this.lastScrollY && currentY > 126
      this.lastScrollY = currentY
    },
    openContact() {
      eventBus.showContactModal = true
    },
    openSidebar() {
      eventBus.sidebarOpen = true
    },
    scrollToTop() {
      if (this.$route.path !== '/') {
        this.$router.push('/')
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    scrollTo(hash) {
      if (this.$route.path !== '/') {
        this.$router.push({ path: '/', hash })
      } else {
        const el = document.querySelector(hash)
        if (el) el.scrollIntoView({ behavior: 'smooth' })
      }
    },
    selectLang(code) {
      this.currentLang = code
      this.langMenuOpen = false
      eventBus.triggerLangChange?.(code)
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500&display=swap');

.app-header {
  background: rgb(255, 255, 255);
  flex-direction: column !important;
  transition: transform 0.3s ease !important;
}

.app-header.header-hidden {
  transform: translateY(-100%) !important;
}

.app-header :deep(.v-toolbar__content) {
  flex-direction: column;
  height: 126px !important;
  max-height: 126px !important;
  padding: 0;
}

.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 1400px !important;
  flex: 1;
}

.logo-section {
  flex-shrink: 0;
}

.logo-link {
  text-decoration: none;
  display: flex;
  align-items: center;
}

.logo-img {
  height: 44px;
  width: auto;
  display: block;
}

.navigation-menu {
  display: flex;
  gap: 34px;
  align-items: center;
}

.nav-link {
  text-decoration: none;
  color: rgb(46, 46, 46);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 22.4px;
  text-align: center;
  transition: color 0.2s ease;
  white-space: nowrap;
}

.nav-link:hover {
  color: rgb(28, 165, 190);
}

.right-section {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-shrink: 0;
}

.contact-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 141px;
  height: 38px;
  background: #1CA5BE;
  color: #F1F1F1;
  border: none;
  border-radius: 4px;
  font-family: "SF Pro", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.03em;
  line-height: 16.8px;
  text-align: center;
  cursor: pointer;
  transition: background 0.2s ease;
}

.contact-btn:hover {
  background: rgb(18, 129, 149);
}

.lang-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  background: rgba(46, 46, 46, 0.2);
  color: rgb(255, 255, 255);
  border: none;
  border-radius: 4px;
  font-family: "SF Pro", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 144.38%;
  text-align: center;
  text-transform: capitalize;
  cursor: pointer;
  transition: background 0.2s ease;
}

.lang-btn:hover {
  background: rgba(46, 46, 46, 0.35);
}

.lang-dropdown {
  background: rgb(46, 46, 46);
  border-radius: 6px;
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  min-width: 130px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.lang-option {
  background: none;
  border: none;
  color: rgb(241, 241, 241);
  font-family: "SF Pro", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 15px;
  font-weight: 400;
  text-align: left;
  padding: 10px 22px 10px 25px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.lang-option:hover {
  background: rgba(255, 255, 255, 0.08);
}

.lang-option.active {
  color: rgb(28, 165, 190);
}

/* Mobile Menu Button */
.menu-btn {
  display: none;
  align-items: center;
  justify-content: center;
  height: 38px;
  padding: 0 24px;
  background: #1CA5BE;
  color: #F1F1F1;
  border: none;
  border-radius: 4px;
  font-family: "SF Pro", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.03em;
  cursor: pointer;
  transition: background 0.2s ease;
}

.menu-btn:hover {
  background: rgb(18, 129, 149);
}

/* Responsive */
@media (max-width: 1280px) {
  .navigation-menu {
    gap: 16px;
  }
}

@media (max-width: 960px) {
  .navigation-menu {
    display: none;
  }

  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: flex !important;
  }
}
</style>

